import React, { useState, useEffect } from 'react';

import { CrossIcon } from '@vlabs/icons';
import { DeleteFacesButton } from '@vlabs/monokit/features/delete-faces';
import { MoveFacesToListButton } from '@vlabs/monokit/features/move-faces-to-list';
import { useGetFaces, useGetFacesCount } from '@vlabs/api/luna-api/client';
import { FaceCard } from '@vlabs/monokit/shared/ui/face-card';
import { BulkActions, Control, PaginationPanel } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import st from './ListDetailsViewer.module.sass';
import { ListFacesFastFilters } from '../../filters/faces-filters';
import { dropdownValueToSorting, sortingToDropdownValue, SortListFacesDropdown } from '../../sort';
import { SwitchListTypeButton } from '../../switch-list-type';
import type { TListFacesSortValue } from '../../types';
import { useListFacesPageFilters } from '../../useListDetailsFilters';

interface ListFacesViewerProps {
  listId: string;
}

export function ListFacesViewer({ listId }: ListFacesViewerProps): React.JSX.Element {
  const { filters, updateFilters } = useListFacesPageFilters();
  const { t } = useTranslation();
  const [selectedFaceIds, setSelectedFaceIds] = useState<string[]>([]);

  const getFacesCount = useGetFacesCount({ list_id: listId, ...filters });
  const getFaces = useGetFaces({ list_id: listId, ...filters });

  useEffect(() => setSelectedFaceIds([]), [getFaces.data]);

  const faces = getFaces?.data?.faces ?? [];
  const facesCount = getFacesCount?.data?.faces_count ?? 0;
  const pageCount = Math.ceil(facesCount / filters.page_size);
  const selectedCount = selectedFaceIds.length;
  const isBulkMode = selectedCount > 0;
  const isAllSelected = faces.length > 0 && selectedCount === faces.length;
  const isIndeterminate = selectedCount > 0 && !isAllSelected;

  const handleChangeSort = (
    value: TListFacesSortValue,
  ) => {
    const nextSorting = dropdownValueToSorting(value);

    updateFilters({
      ...nextSorting,
      page: 0,
    });
  };

  const handleToggleAll = () => {
    if (isAllSelected) {
      setSelectedFaceIds([]);
      return;
    }

    setSelectedFaceIds(faces.map(({ face_id }) => face_id));
  };

  const handleToggleFace = (faceId: string) => {
    setSelectedFaceIds((prev) => (
      prev.includes(faceId)
        ? prev.filter((id) => id !== faceId)
        : [...prev, faceId]
    ));
  };

  return (
    <div className={st.Root}>
      {!isBulkMode && (
        <div className={st.Toolbar}>
          <div className={st.ToolbarContent}>
            <SwitchListTypeButton />

            <div className={st.Count}>
              {t('list-details-page:titles.quantity', { context: 'faces', count: facesCount })}
            </div>

            <ListFacesFastFilters />
          </div>

          <SortListFacesDropdown
            onChange={handleChangeSort}
            // FIXME: выкинуть конвертеры, передавать в value обьект нужного вида
            value={sortingToDropdownValue(filters.sort, filters.order)}
          />
        </div>
      )}

      {!!isBulkMode && (
        <div className={st.BulkActions}>
          <BulkActions
            data-testid="FacesBulkActions"
            indeterminate={isIndeterminate}
            onCheckboxClick={handleToggleAll}
          >
            <div className={st.FacesBulkActions}>
              <span
                className={st.SelectedCount}
                data-testid="FacesBulkActions-SelectedCount"
              >
                {t('list-details-page:cards_bulk_actions.selected_count', { count: selectedCount })}
              </span>
              <div className={st.BulkActionsControls}>
                <MoveFacesToListButton
                  faceIds={selectedFaceIds}
                />
                <DeleteFacesButton
                  faceIds={selectedFaceIds}
                  size="small"
                  variant="flat-inverted"
                />
                <Control.IconButton
                  data-testid="FacesBulkActions-CancelBtn"
                  icon={<CrossIcon />}
                  kind="secondary"
                  onClick={() => setSelectedFaceIds([])}
                  shape="square"
                  size="small"
                  variant="flat-inverted"
                />
              </div>
            </div>
          </BulkActions>
        </div>
      )}

      <div className={st.Content}>
        <div className={st.GridList}>
          {faces.map((face) => (
            <FaceCard
              faceId={face.face_id}
              isSelected={selectedFaceIds.includes(face.face_id)}
              key={face.face_id}
              onSelect={() => handleToggleFace(face.face_id)}
              selectable
              size="small"
            />
          ))}
        </div>
      </div>

      <div className={st.Pagination}>
        <PaginationPanel
          canNextPage={filters.page + 1 < pageCount}
          canPreviousPage={filters.page > 0}
          gotoPage={(page) => updateFilters({ page })}
          nextPage={() => updateFilters({ page: filters.page + 1 })}
          pageCount={pageCount}
          pageIndex={filters.page}
          pageSize={filters.page_size}
          previousPage={() => updateFilters({ page: filters.page - 1 })}
          setPageSize={(page_size: number) => updateFilters({ page_size, page: 0 })}
        />
      </div>
    </div>
  );
}
